import { useCallback, useState } from 'react';

import type { RegistryInboundRow, RegistryOrderDetail } from '@/types/registry-documents';

/** The order a person chose for an email: its id for the API, and what
 * names it on screen afterwards. */
export interface RegistryInboundAttachTarget {
  orderId: string;
  reference: string;
  reportLabel: string | null;
}

/**
 * The "Attach to order" dialog of the unmatched-mail list: which email it
 * is open for, and what its confirm does.
 *
 * One dialog per list rather than one per row, as for closing an order:
 * it outlives the row, which leaves the open view the moment its email is
 * attached. The email stays set after the dialog is dismissed: its exit
 * transition still shows it.
 *
 * `attach` is the list's own action (it re-reads the list); a failure is
 * re-thrown for the dialog to show, and leaves it open.
 */
export function useRegistryInboundAttachDialog(
  attach: (rowId: string, orderId: string) => Promise<RegistryOrderDetail>,
  onAttached?: (
    row: RegistryInboundRow,
    target: RegistryInboundAttachTarget,
    order: RegistryOrderDetail
  ) => void
) {
  const [row, setRow] = useState<RegistryInboundRow | null>(null);
  const [opened, setOpened] = useState(false);

  const open = useCallback((target: RegistryInboundRow) => {
    setRow(target);
    setOpened(true);
  }, []);

  const dismiss = useCallback(() => setOpened(false), []);

  const confirm = useCallback(
    async (target: RegistryInboundAttachTarget) => {
      if (!row) {
        return;
      }
      const order = await attach(row.id, target.orderId);
      setOpened(false);
      onAttached?.(row, target, order);
    },
    [row, attach, onAttached]
  );

  return {
    /** Open the dialog for an email. */
    open,
    /** Spread onto `RegistryInboundAttachModal`. */
    modalProps: { opened, row, onClose: dismiss, onConfirm: confirm },
  };
}
